import { useState } from "react";
import { Download, Upload, RotateCcw, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";

const hslToHex = (hsl: string): string => {
  const [h, s, l] = hsl.split(' ').map(v => parseFloat(v.replace('%', '')));
  const hDecimal = h / 360;
  const sDecimal = s / 100;
  const lDecimal = l / 100;

  let r, g, b;
  if (sDecimal === 0) {
    r = g = b = lDecimal;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = lDecimal < 0.5 ? lDecimal * (1 + sDecimal) : lDecimal + sDecimal - lDecimal * sDecimal;
    const p = 2 * lDecimal - q;
    r = hue2rgb(p, q, hDecimal + 1/3);
    g = hue2rgb(p, q, hDecimal);
    b = hue2rgb(p, q, hDecimal - 1/3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hexToHsl = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const colorPresets = {
  shomri: {
    name: "Shomri",
    colors: {
      primary: '251 76% 70%',
      primaryForeground: '0 0% 100%',
      secondary: '201 81% 92%',
      secondaryForeground: '234 37% 23%',
      accent: '251 76% 92%',
      accentForeground: '234 37% 23%',
      background: '201 50% 98%',
      foreground: '234 37% 23%',
      card: '201 50% 99%',
      cardForeground: '234 37% 23%',
      muted: '201 60% 90%',
      mutedForeground: '234 20% 45%',
      border: '251 40% 88%',
      destructive: '342 76% 68%',
      destructiveForeground: '0 0% 100%',
      success: '146 42% 56%',
      successForeground: '0 0% 100%',
    }
  },
  lightBlue: {
    name: "Light Blue",
    colors: {
      secondary: '200 90% 94%',
      accent: '205 90% 93%',
      background: '210 100% 98%',
      card: '210 100% 99%',
      muted: '200 85% 92%',
      border: '200 80% 88%',
    }
  },
  gray: {
    name: "Gray",
    colors: {
      secondary: '210 40% 96.1%',
      accent: '210 40% 96.1%',
      background: '0 0% 100%',
      card: '0 0% 100%',
      muted: '210 40% 96.1%',
      border: '214.3 31.8% 91.4%',
    }
  },
  purple: {
    name: "Purple",
    colors: {
      secondary: '270 85% 94%',
      accent: '275 85% 93%',
      background: '270 100% 98%',
      card: '270 100% 99%',
      muted: '270 80% 92%',
      border: '270 75% 88%',
    }
  }
};

export const ThemeConfiguratorSection = () => {
  const { theme, updateTheme, resetTheme, exportTheme, importTheme } = useTheme();
  const [importText, setImportText] = useState("");

  const handleColorChange = (key: keyof typeof theme, hex: string) => {
    const hsl = hexToHsl(hex);
    updateTheme({ [key]: hsl });
  };

  const handleExport = () => {
    const json = exportTheme();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'theme-config.json';
    a.click();
    toast.success("Theme exported successfully");
  };

  const handleImport = () => {
    try {
      importTheme(importText);
      setImportText("");
      toast.success("Theme imported successfully");
    } catch (error) {
      toast.error("Invalid theme format");
    }
  };

  const handleReset = () => {
    resetTheme();
    toast.success("Theme reset to defaults");
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground mb-2 flex items-center gap-2">
          <Palette className="w-6 h-6" />
          Theme Configurator
        </h2>
        <p className="text-muted-foreground">
          Customize the visual appearance of your application in real-time.
        </p>
      </div>

      <div className="flex gap-2">
        <Button onClick={handleExport} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export Theme
        </Button>
        <Button onClick={handleReset} variant="outline" size="sm">
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset to Default
        </Button>
      </div>

      <Tabs defaultValue="colors" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="spacing">Spacing</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="colors" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Color Presets</CardTitle>
              <CardDescription>Quick color schemes to try</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(colorPresets).map(([key, preset]) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    onClick={() => updateTheme(preset.colors)}
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Primary Colors</CardTitle>
              <CardDescription>Main brand colors used throughout the app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary">Primary</Label>
                  <Input
                    id="primary"
                    type="color"
                    value={hslToHex(theme.primary)}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="primaryForeground">Primary Foreground</Label>
                  <Input
                    id="primaryForeground"
                    type="color"
                    value={hslToHex(theme.primaryForeground)}
                    onChange={(e) => handleColorChange('primaryForeground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Secondary Colors</CardTitle>
              <CardDescription>Secondary UI elements and accents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="secondary">Secondary</Label>
                  <Input
                    id="secondary"
                    type="color"
                    value={hslToHex(theme.secondary)}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryForeground">Secondary Foreground</Label>
                  <Input
                    id="secondaryForeground"
                    type="color"
                    value={hslToHex(theme.secondaryForeground)}
                    onChange={(e) => handleColorChange('secondaryForeground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accent">Accent</Label>
                  <Input
                    id="accent"
                    type="color"
                    value={hslToHex(theme.accent)}
                    onChange={(e) => handleColorChange('accent', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accentForeground">Accent Foreground</Label>
                  <Input
                    id="accentForeground"
                    type="color"
                    value={hslToHex(theme.accentForeground)}
                    onChange={(e) => handleColorChange('accentForeground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Background & Text</CardTitle>
              <CardDescription>Page backgrounds and text colors</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="background">Background</Label>
                  <Input
                    id="background"
                    type="color"
                    value={hslToHex(theme.background)}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="foreground">Foreground</Label>
                  <Input
                    id="foreground"
                    type="color"
                    value={hslToHex(theme.foreground)}
                    onChange={(e) => handleColorChange('foreground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="muted">Muted</Label>
                  <Input
                    id="muted"
                    type="color"
                    value={hslToHex(theme.muted)}
                    onChange={(e) => handleColorChange('muted', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mutedForeground">Muted Foreground</Label>
                  <Input
                    id="mutedForeground"
                    type="color"
                    value={hslToHex(theme.mutedForeground)}
                    onChange={(e) => handleColorChange('mutedForeground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status Colors</CardTitle>
              <CardDescription>Colors for success, error, and warning states</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="success">Success</Label>
                  <Input
                    id="success"
                    type="color"
                    value={hslToHex(theme.success)}
                    onChange={(e) => handleColorChange('success', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="successForeground">Success Foreground</Label>
                  <Input
                    id="successForeground"
                    type="color"
                    value={hslToHex(theme.successForeground)}
                    onChange={(e) => handleColorChange('successForeground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destructive">Destructive</Label>
                  <Input
                    id="destructive"
                    type="color"
                    value={hslToHex(theme.destructive)}
                    onChange={(e) => handleColorChange('destructive', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destructiveForeground">Destructive Foreground</Label>
                  <Input
                    id="destructiveForeground"
                    type="color"
                    value={hslToHex(theme.destructiveForeground)}
                    onChange={(e) => handleColorChange('destructiveForeground', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="spacing" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Border Radius</CardTitle>
              <CardDescription>Control the roundness of components</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Radius</Label>
                  <span className="text-sm text-muted-foreground">{theme.radius}px</span>
                </div>
                <Slider
                  value={[theme.radius]}
                  onValueChange={([value]) => updateTheme({ radius: value })}
                  min={0}
                  max={24}
                  step={1}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Font Size</CardTitle>
              <CardDescription>Base font size for the entire application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Base Size</Label>
                  <span className="text-sm text-muted-foreground">{theme.fontSize}px</span>
                </div>
                <Slider
                  value={[theme.fontSize]}
                  onValueChange={([value]) => updateTheme({ fontSize: value })}
                  min={12}
                  max={20}
                  step={1}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Component Preview</CardTitle>
              <CardDescription>See how your theme looks on real components</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-medium mb-3">Buttons</h3>
                <div className="flex flex-wrap gap-2">
                  <Button>Primary Button</Button>
                  <Button variant="secondary">Secondary Button</Button>
                  <Button variant="outline">Outline Button</Button>
                  <Button variant="destructive">Destructive Button</Button>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">Badges</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge>Default Badge</Badge>
                  <Badge variant="secondary">Secondary Badge</Badge>
                  <Badge variant="outline">Outline Badge</Badge>
                  <Badge variant="destructive">Destructive Badge</Badge>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">Cards</h3>
                <Card className="max-w-sm">
                  <CardHeader>
                    <CardTitle>Card Title</CardTitle>
                    <CardDescription>This is a card description showing the theme.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-foreground">
                      Card content goes here with the current theme applied.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">Inputs</h3>
                <div className="space-y-2 max-w-sm">
                  <Input placeholder="Regular input field" />
                  <Input placeholder="Disabled input" disabled />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Import Theme</CardTitle>
              <CardDescription>Paste a theme JSON configuration to import</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste theme JSON here..."
                className="w-full h-32 p-3 border rounded-md bg-background text-foreground font-mono text-sm"
              />
              <Button onClick={handleImport} disabled={!importText}>
                <Upload className="w-4 h-4 mr-2" />
                Import Theme
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
